import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Wrench, Radio, Music, RadioTower, RefreshCcw, KeyRound,
  CheckCircle2, XCircle, AlertTriangle, Send, Minus, Plus, Search, AppIcon, ChevronDown, Copy,
} from '../../ui/icons';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import SearchableSelect from '../../ui/SearchableSelect';
import EmailChipsInput from '../../ui/EmailChipsInput';
import Colors, { withAlpha } from '../../../constants/Colors';
import { copyToClipboard } from '../../../utils/clipboard';
import {
  opsManualApi,
  opsHealthApi,
  gamesApi,
  OpsEnv,
  Game,
  OpsCheckAccessResponse,
  OpsPurchasedProduct,
  THEME_MODE,
} from '../../../services/api';

const ACTIVE_GAMES_POLL_MS = 30000;

function ActiveGamesAccordion({ games, loading }: { games: string[]; loading: boolean }) {
  const [open, setOpen] = useState(true);

  return (
    <div
      style={{
        border: '1px solid rgba(var(--color-border-rgb), 0.16)',
        borderRadius: '12px',
        background: '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '12px 14px',
          background: 'none',
          border: 0,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: 700, color: Colors.text }}>
          Active Games {loading ? '' : `(${games.length})`}
        </span>
        <ChevronDown
          size={16}
          style={{ color: Colors.textLight, transition: 'transform 0.15s ease', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
        />
      </button>
      {open && (
        <div style={{ borderTop: '1px solid rgba(var(--color-border-rgb), 0.12)' }}>
          {loading && games.length === 0 ? (
            <div style={{ padding: '14px', fontSize: '12.5px', color: Colors.textLight }}>Loading…</div>
          ) : games.length === 0 ? (
            <div style={{ padding: '14px', fontSize: '12.5px', color: Colors.textLight }}>No active games right now.</div>
          ) : (
            games.map((name, i) => (
              <div
                key={name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  padding: '10px 14px',
                  borderBottom: i < games.length - 1 ? '1px solid rgba(var(--color-border-rgb), 0.08)' : 'none',
                }}
              >
                <span
                  title={name}
                  style={{
                    fontSize: '12px',
                    fontFamily: "'SF Mono', monospace",
                    color: Colors.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {name}
                </span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(name, 'Game name copied')}
                  title="Copy game name"
                  style={{
                    flex: 'none',
                    width: '22px',
                    height: '22px',
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    display: 'grid',
                    placeItems: 'center',
                    cursor: 'pointer',
                    color: Colors.inactive,
                    borderRadius: '4px',
                  }}
                >
                  <Copy size={13} />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

type OpKey = 'launch-mqtt' | 'launch-audio' | 'launch-tipoff' | 'update-tipoff' | 'grant-access';

const OPS: { key: OpKey; label: string; icon: AppIcon; actionLabel: string }[] = [
  { key: 'launch-mqtt', label: 'Launch Bot', icon: Radio, actionLabel: 'Launch' },
  { key: 'launch-audio', label: 'Launch Audio', icon: Music, actionLabel: 'Launch' },
  { key: 'launch-tipoff', label: 'Launch Tipoff', icon: RadioTower, actionLabel: 'Launch' },
  { key: 'update-tipoff', label: 'Update Tipoff', icon: RefreshCcw, actionLabel: 'Execute' },
  { key: 'grant-access', label: 'Grant Access', icon: KeyRound, actionLabel: 'Execute' },
];

type ResultTone = 'success' | 'warning' | 'error';

interface OpResult {
  tone: ResultTone;
  message: string;
  raw: unknown;
  kind?: 'check-access' | 'generic';
  checkResults?: OpsCheckAccessResponse[];
}

// Same label-left field-row layout as ManualOperationsPanel's (IAP Notification) form, so
// every form in the Ops section lines up the same way.
const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '8px 14px',
};

const labelStyle: React.CSSProperties = {
  flex: '0 0 128px',
  fontSize: '13px',
  fontWeight: 500,
  color: Colors.text,
  textAlign: 'right',
};

const fieldStyle: React.CSSProperties = {
  flex: '0 1 420px',
  minWidth: 0,
};

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={rowStyle}>
      <label style={labelStyle}>{label}</label>
      <div style={fieldStyle}>{children}</div>
    </div>
  );
}

const RESULT_TONE_STYLE: Record<ResultTone, { bg: string; fg: string; icon: AppIcon }> = {
  success: { bg: withAlpha(Colors.success, 0.12), fg: Colors.success, icon: CheckCircle2 },
  warning: { bg: 'rgba(255, 149, 0, 0.12)', fg: '#FF9500', icon: AlertTriangle },
  error: { bg: withAlpha(Colors.error, 0.12), fg: Colors.error, icon: XCircle },
};

function formatExpiry(epoch?: number | null): string {
  if (!epoch) return '—';
  const date = new Date(epoch * 1000);
  if (Number.isNaN(date.getTime())) return String(epoch);
  return date.toLocaleString();
}

function productLabel(product: OpsPurchasedProduct): string {
  const id = product.product_id || '';
  if (id.includes('all_sports')) return 'All Sports Pass';
  if (id.includes('football_season')) return 'Football Season Pass';
  if (id.includes('basketball_season')) return 'Basketball Season Pass';
  if (id.includes('single_game')) return 'Single Game';
  return product.product || product.product_id || 'Product';
}

function statusBadgeStyle(status: string): { bg: string; fg: string } {
  const normalized = status.trim().toLowerCase();
  if (normalized === 'active' || normalized === 'cancelled' || normalized === 'canceled') {
    return { bg: withAlpha(Colors.success, 0.14), fg: Colors.success };
  }
  if (normalized === 'expired') return { bg: withAlpha(Colors.error, 0.12), fg: Colors.error };
  if (normalized === 'has_access') return { bg: withAlpha(Colors.success, 0.14), fg: Colors.success };
  if (normalized === 'no_access') return { bg: 'rgba(255, 149, 0, 0.14)', fg: '#FF9500' };
  return { bg: 'rgba(255, 149, 0, 0.14)', fg: '#FF9500' };
}

// Dev-mode admin builds can target dev/qa infra directly; any other build (i.e. the real prod
// deployment) has no environment choice - it always targets prod - so the selector is hidden.
const showEnvSelector = THEME_MODE === 'dev';

const TIMESTAMP_HOLD_DELAY_MS = 400;
const TIMESTAMP_HOLD_INTERVAL_MS = 100;

// setUTCSeconds rolls minutes/hours/days/months/years over on its own, and leaves the
// millisecond value untouched when called with a single argument.
function adjustTimestampSeconds(iso: string, deltaSeconds: number): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  date.setUTCSeconds(date.getUTCSeconds() + deltaSeconds);
  return date.toISOString();
}

export default function ManualOperationPanel() {
  const [selectedOp, setSelectedOp] = useState<OpKey>('launch-mqtt');
  const [env, setEnv] = useState<OpsEnv>(showEnvSelector ? 'dev' : 'prod');

  // launch-mqtt / launch-tipoff / update-tipoff shared
  const [gameName, setGameName] = useState('');
  const [gameType, setGameType] = useState<'football' | 'basketball'>('football');
  // launch-mqtt
  const [botId, setBotId] = useState('');
  // launch-audio / launch-tipoff / update-tipoff (audio) - sent as audio_type for tipoffs
  const [direction, setDirection] = useState<'home' | 'away'>('home');
  const [segmentDuration, setSegmentDuration] = useState('1');
  // launch-audio / launch-tipoff / update-tipoff (audio)
  const [streamUrl, setStreamUrl] = useState('');
  // launch-tipoff
  const [detectionMode, setDetectionMode] = useState<'kickoff_tipoff' | 'halftime_resumption'>('kickoff_tipoff');
  // update-tipoff
  const [tipoffType, setTipoffType] = useState<'genius' | 'audio'>('genius');
  const [timestamp, setTimestamp] = useState(() => new Date().toISOString());
  // Timestamp as last fetched from the DB (or last set by a fetch) - compared against the
  // live `timestamp` value to show the admin what they've adjusted it by.
  const [originalTimestamp, setOriginalTimestamp] = useState<string | null>(null);
  // Fetched alongside originalTimestamp so the admin can see whether this record is already a
  // prior manual correction, and what kind of detection (if any) produced it.
  const [originalDetectionType, setOriginalDetectionType] = useState<string | null>(null);
  const [originalIsManualOverride, setOriginalIsManualOverride] = useState(false);
  const [fetchingTipoff, setFetchingTipoff] = useState(false);
  const [fetchTipoffError, setFetchTipoffError] = useState('');
  // grant-access
  const [emails, setEmails] = useState<string[]>([]);
  const [gameOptions, setGameOptions] = useState<Game[]>([]);
  const [gamesLoading, setGamesLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [result, setResult] = useState<OpResult | null>(null);

  // Games with a currently-running bot/audio/tipoff ECS task.
  const [activeGameNames, setActiveGameNames] = useState<string[]>([]);
  const [activeGamesLoading, setActiveGamesLoading] = useState(false);

  const timestampHoldTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timestampHoldIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const adjustTimestamp = (deltaSeconds: number) => {
    setTimestamp((prev) => adjustTimestampSeconds(prev, deltaSeconds));
  };

  const stopTimestampHold = () => {
    if (timestampHoldTimeoutRef.current) {
      clearTimeout(timestampHoldTimeoutRef.current);
      timestampHoldTimeoutRef.current = null;
    }
    if (timestampHoldIntervalRef.current) {
      clearInterval(timestampHoldIntervalRef.current);
      timestampHoldIntervalRef.current = null;
    }
  };

  // First click adjusts immediately; holding kicks in a beat later so a normal click
  // doesn't also trigger the repeat-adjust interval.
  const startTimestampHold = (deltaSeconds: number) => {
    adjustTimestamp(deltaSeconds);
    timestampHoldTimeoutRef.current = setTimeout(() => {
      timestampHoldIntervalRef.current = setInterval(() => adjustTimestamp(deltaSeconds), TIMESTAMP_HOLD_INTERVAL_MS);
    }, TIMESTAMP_HOLD_DELAY_MS);
  };

  useEffect(() => stopTimestampHold, []);

  // A previously fetched timestamp belongs to a specific game/type/tipoff-type combo - once
  // any of those change it's no longer valid, so drop it rather than show a stale "Original".
  useEffect(() => {
    setOriginalTimestamp(null);
    setOriginalDetectionType(null);
    setOriginalIsManualOverride(false);
    setFetchTipoffError('');
  }, [gameName, gameType, tipoffType, direction]);

  const handleFetchTipoff = async () => {
    setFetchingTipoff(true);
    setFetchTipoffError('');
    const response = await opsHealthApi.getTipoffs(gameName.trim(), gameType, env, direction);
    setFetchingTipoff(false);

    const record = response.data?.tipoffs?.[tipoffType];
    if (response.success && record?.created_at) {
      setTimestamp(record.created_at);
      setOriginalTimestamp(record.created_at);
      setOriginalDetectionType(record.detection_type || null);
      setOriginalIsManualOverride(record.is_manual_override);
    } else if (response.success) {
      setFetchTipoffError(`No existing ${tipoffType === 'audio' ? `${direction} audio` : tipoffType} tipoff record found for this game.`);
    } else {
      setFetchTipoffError(response.error || 'Failed to fetch tipoff timestamp');
    }
  };

  const loadScheduledGames = useCallback(async () => {
    setGamesLoading(true);
    const response = await gamesApi.getScheduledTodayAndUpcomingGames();
    const scheduledGames = (response.success && response.data ? response.data.games || [] : []).filter(
      (game) => String(game.status || '').toLowerCase() === 'scheduled'
    );
    setGameOptions(scheduledGames);
    setGamesLoading(false);
  }, []);

  useEffect(() => {
    loadScheduledGames();
  }, [loadScheduledGames]);

  const loadActiveGames = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setActiveGamesLoading(true);
    const response = await opsHealthApi.getEcsTasks();
    if (response.success && response.data?.success) {
      const names = Array.from(
        new Set((response.data.tasks || []).map((t) => t.game_name).filter(Boolean))
      ).sort();
      setActiveGameNames(names);
    }
    if (showSpinner) setActiveGamesLoading(false);
  }, []);

  useEffect(() => {
    loadActiveGames(true);
    const timer = setInterval(() => loadActiveGames(false), ACTIVE_GAMES_POLL_MS);
    return () => clearInterval(timer);
  }, [loadActiveGames]);

  const gameSelectOptions = useMemo(
    () =>
      gameOptions.map((game) => ({
        value: game.game_name,
        label: `${game.home_team} vs ${game.away_team} (${game.date})`,
        searchText: `${game.game_name} ${game.home_team} ${game.away_team} ${game.venue || ''} ${game.date}`,
      })),
    [gameOptions]
  );

  const resetResult = () => setResult(null);

  const selectOp = (key: OpKey) => {
    setSelectedOp(key);
    resetResult();
  };

  const toneFromLaunchStatus = (status: string): ResultTone => {
    if (status === 'launched') return 'success';
    if (status === 'blocked') return 'warning';
    return 'error';
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    resetResult();

    if (selectedOp === 'launch-mqtt') {
      const response = await opsManualApi.launchMqtt({
        env,
        game_name: gameName,
        game_type: gameType,
        bot_id: botId,
        run_duration_hours: '4',
      });
      if (response.success && response.data) {
        const tone = toneFromLaunchStatus(response.data.status);
        setResult({
          tone,
          message: response.data.status === 'launched'
            ? `MQTT task launched (task: ${response.data.task_arn})`
            : response.data.error || `Status: ${response.data.status}`,
          raw: response.data,
        });
      } else {
        setResult({ tone: 'error', message: response.error || 'Failed to launch MQTT task', raw: response });
      }
    } else if (selectedOp === 'launch-audio') {
      const response = await opsManualApi.launchAudio({
        env,
        game_name: `${direction}-${gameName}`,
        stream_url: streamUrl,
        segment_duration: segmentDuration || '1',
        playlist_size: '10000',
      });
      if (response.success && response.data) {
        const tone = toneFromLaunchStatus(response.data.status);
        setResult({
          tone,
          message: response.data.status === 'launched'
            ? `Audio task launched (task: ${response.data.task_arn})`
            : response.data.error || `Status: ${response.data.status}`,
          raw: response.data,
        });
      } else {
        setResult({ tone: 'error', message: response.error || 'Failed to launch audio task', raw: response });
      }
    } else if (selectedOp === 'launch-tipoff') {
      const response = await opsManualApi.launchTipoff({
        env,
        game_name: gameName,
        game_type: gameType,
        stream_url: streamUrl,
        max_runtime_minutes: '120',
        detection_mode: detectionMode,
        audio_type: direction,
      });
      if (response.success && response.data) {
        const tone = toneFromLaunchStatus(response.data.status);
        setResult({
          tone,
          message: response.data.status === 'launched'
            ? `Tipoff task launched (task: ${response.data.task_arn})`
            : response.data.error || `Status: ${response.data.status}`,
          raw: response.data,
        });
      } else {
        setResult({ tone: 'error', message: response.error || 'Failed to launch tipoff task', raw: response });
      }
    } else if (selectedOp === 'update-tipoff') {
      // No stream_url from this form for audio tipoffs - the backend keeps the existing record's
      // URL, or falls back to the CloudFront URL for {direction}-{game_id} when there is none.
      const response = await opsManualApi.updateTipoff({
        env,
        game_id: gameName.trim(),
        game_type: gameType,
        tipoff_type: tipoffType,
        timestamp,
        ...(tipoffType === 'audio' ? { audio_type: direction } : {}),
      });
      if (response.success && response.data) {
        if (response.data.status === 'success') {
          setOriginalTimestamp(timestamp);
          setOriginalDetectionType(tipoffType === 'audio' ? 'manual_override' : null);
          setOriginalIsManualOverride(tipoffType === 'audio');
        }
        setResult({
          tone: response.data.status === 'success' ? 'success' : 'error',
          message: response.data.status === 'success'
            ? `Tipoff record written (detection_id: ${response.data.detection_id})`
            : response.data.error || 'Failed to update tipoff',
          raw: response.data,
        });
      } else {
        setResult({ tone: 'error', message: response.error || 'Failed to update tipoff', raw: response });
      }
    } else if (selectedOp === 'grant-access') {
      const grantResults = [];
      for (const email of emails) {
        const response = await opsManualApi.grantAccess({
          env,
          email,
          game_name: gameName,
        });
        grantResults.push({ email, response });
      }

      const granted = grantResults.filter((item) => item.response.success && item.response.data?.status === 'granted');
      const already = grantResults.filter((item) => item.response.success && item.response.data?.status === 'already_has_access');
      const failed = grantResults.filter((item) => !item.response.success || item.response.data?.status === 'error');

      const tone: ResultTone = failed.length > 0 ? 'error' : already.length > 0 && granted.length === 0 ? 'warning' : 'success';
      setResult({
        tone,
        message: [
          granted.length ? `Granted: ${granted.length}` : null,
          already.length ? `Already had access: ${already.length}` : null,
          failed.length ? `Failed: ${failed.length}` : null,
        ].filter(Boolean).join(' · '),
        raw: grantResults.map((item) => item.response.data || { email: item.email, error: item.response.error }),
      });
    }

    setSubmitting(false);
  };

  const handleCheckAccess = async () => {
    if (emails.length === 0) return;
    setCheckingAccess(true);
    resetResult();
    try {
      const checkResults: OpsCheckAccessResponse[] = [];
      for (const email of emails) {
        const response = await opsManualApi.checkAccess({ env, email, game_name: gameName.trim() || undefined });
        if (response.success && response.data) {
          checkResults.push(response.data);
        } else {
          checkResults.push({
            success: false,
            status: 'no_access',
            email,
            user_id: null,
            products: [],
            error: typeof response.error === 'string' ? response.error : 'Failed to check access',
          });
        }
      }

      const withAccess = checkResults.filter((item) => item.status === 'has_access').length;
      const withoutAccess = checkResults.length - withAccess;
      const tone: ResultTone = withAccess > 0 && withoutAccess === 0 ? 'success' : withAccess > 0 ? 'warning' : 'warning';

      setResult({
        tone,
        kind: 'check-access',
        message:
          checkResults.length === 1
            ? checkResults[0].status === 'has_access'
              ? `${checkResults[0].email} has access`
              : `No access found for ${checkResults[0].email}`
            : `Checked ${checkResults.length} emails · ${withAccess} with access · ${withoutAccess} without`,
        raw: checkResults[0],
        checkResults,
      });
    } catch (error) {
      setResult({
        tone: 'error',
        kind: 'check-access',
        message: error instanceof Error ? error.message : 'Failed to check access',
        raw: error,
        checkResults: [],
      });
    } finally {
      setCheckingAccess(false);
    }
  };

  const isFormValid = (): boolean => {
    if (selectedOp === 'launch-mqtt') return !!gameName.trim() && !!botId.trim();
    if (selectedOp === 'launch-audio') return !!gameName.trim() && !!streamUrl.trim();
    if (selectedOp === 'launch-tipoff') return !!gameName.trim() && !!streamUrl.trim();
    if (selectedOp === 'update-tipoff') return !!gameName.trim() && !!timestamp.trim();
    if (selectedOp === 'grant-access') return emails.length > 0 && !!gameName.trim();
    return false;
  };

  const activeOp = OPS.find((o) => o.key === selectedOp)!;
  const ResultIcon = result ? RESULT_TONE_STYLE[result.tone].icon : null;

  return (
    <div className="section">
      <h2 className="section-title">
        <Wrench size={24} />
        Manual Operation
      </h2>

      <Card
        title="Manual Operation"
        description="Launch Bot/audio/tipoff ECS tasks, override tipoff detections, or manage single-game access"
      >
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: '0 0 170px' }}>
            {OPS.map((op) => {
              const active = op.key === selectedOp;
              return (
                <button
                  key={op.key}
                  type="button"
                  onClick={() => selectOp(op.key)}
                  className={`glass-option ${active ? 'glass-option--active' : ''}`}
                >
                  <op.icon size={18} />
                  <span style={{ fontSize: '12.5px', fontWeight: 700, color: active ? Colors.secondary : Colors.text }}>{op.label}</span>
                </button>
              );
            })}
          </div>

          <div style={{ alignSelf: 'stretch', width: '1px', background: 'rgba(var(--color-border-rgb), 0.16)' }} />

          {/* Form block centred between the two dividers (label 128 + gap 14 + field 420). */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', textAlign: 'left', width: '100%', maxWidth: '562px' }}>
            {selectedOp === 'launch-mqtt' && (
              <>
                {showEnvSelector && (
                  <FieldRow label="Environment">
                    <select className="form-input" value={env} onChange={(e) => setEnv(e.target.value as OpsEnv)}>
                      <option value="dev">dev</option>
                      <option value="qa">qa</option>
                    </select>
                  </FieldRow>
                )}
                <FieldRow label="Game Name">
                  <input className="form-input" value={gameName} onChange={(e) => setGameName(e.target.value)} placeholder="e.g. blue-devils-vs-legacy-fc-20260820" />
                </FieldRow>
                <FieldRow label="Game Type">
                  <select className="form-input" value={gameType} onChange={(e) => setGameType(e.target.value as 'football' | 'basketball')}>
                    <option value="football">football</option>
                    <option value="basketball">basketball</option>
                  </select>
                </FieldRow>
                <FieldRow label="Bot ID">
                  <input className="form-input" value={botId} onChange={(e) => setBotId(e.target.value)} placeholder="e.g. bot-fb-114" />
                </FieldRow>
                <FieldRow label="Run Duration (hours)">
                  <input className="form-input" value="4" readOnly disabled />
                </FieldRow>
              </>
            )}

            {selectedOp === 'launch-audio' && (
              <>
                {showEnvSelector && (
                  <FieldRow label="Environment">
                    <select className="form-input" value={env} onChange={(e) => setEnv(e.target.value as OpsEnv)}>
                      <option value="dev">dev</option>
                      <option value="qa">qa</option>
                    </select>
                  </FieldRow>
                )}
                <FieldRow label="Game Name">
                  <input className="form-input" value={gameName} onChange={(e) => setGameName(e.target.value)} placeholder="e.g. blue-devils-vs-legacy-fc-20260820" />
                </FieldRow>
                <FieldRow label="Direction">
                  <select className="form-input" value={direction} onChange={(e) => setDirection(e.target.value as 'home' | 'away')}>
                    <option value="home">home</option>
                    <option value="away">away</option>
                  </select>
                </FieldRow>
                <FieldRow label="Stream URL">
                  <input className="form-input" value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)} placeholder="https://stream.example.com/audio.m3u8" />
                </FieldRow>
                {showEnvSelector && (
                  <FieldRow label="Segment Duration (s)">
                    <input
                      className="form-input"
                      type="number"
                      min="1"
                      value={segmentDuration}
                      onChange={(e) => setSegmentDuration(e.target.value)}
                    />
                  </FieldRow>
                )}
              </>
            )}

            {selectedOp === 'launch-tipoff' && (
              <>
                {showEnvSelector && (
                  <FieldRow label="Environment">
                    <select className="form-input" value={env} onChange={(e) => setEnv(e.target.value as OpsEnv)}>
                      <option value="dev">dev</option>
                      <option value="qa">qa</option>
                    </select>
                  </FieldRow>
                )}
                <FieldRow label="Game Name">
                  <input className="form-input" value={gameName} onChange={(e) => setGameName(e.target.value)} placeholder="e.g. blue-devils-vs-legacy-fc-20260820" />
                </FieldRow>
                <FieldRow label="Game Type">
                  <select className="form-input" value={gameType} onChange={(e) => setGameType(e.target.value as 'football' | 'basketball')}>
                    <option value="football">football</option>
                    <option value="basketball">basketball</option>
                  </select>
                </FieldRow>
                <FieldRow label="Direction">
                  <select className="form-input" value={direction} onChange={(e) => setDirection(e.target.value as 'home' | 'away')}>
                    <option value="home">home</option>
                    <option value="away">away</option>
                  </select>
                </FieldRow>
                <FieldRow label="Stream URL (CloudFront)">
                  <input className="form-input" value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)} placeholder={`https://<distribution>.cloudfront.net/${direction}-<game>/stream.m3u8`} />
                </FieldRow>
                <FieldRow label="Detection mode">
                  <select className="form-input" value={detectionMode} onChange={(e) => setDetectionMode(e.target.value as 'kickoff_tipoff' | 'halftime_resumption')}>
                    <option value="kickoff_tipoff">kickoff_tipoff</option>
                    <option value="halftime_resumption">halftime_resumption</option>
                  </select>
                </FieldRow>
                <FieldRow label="Max Runtime (minutes)">
                  <input className="form-input" value="120" readOnly disabled />
                </FieldRow>
              </>
            )}

            {selectedOp === 'update-tipoff' && (
              <>
                {showEnvSelector && (
                  <FieldRow label="Environment">
                    <select className="form-input" value={env} onChange={(e) => setEnv(e.target.value as OpsEnv)}>
                      <option value="dev">dev</option>
                      <option value="qa">qa</option>
                    </select>
                  </FieldRow>
                )}
                <FieldRow label="Game Name">
                  <input className="form-input" value={gameName} onChange={(e) => setGameName(e.target.value)} placeholder="e.g. blue-devils-vs-tulane-green-wave-20260819" />
                </FieldRow>
                <FieldRow label="Game Type">
                  <select className="form-input" value={gameType} onChange={(e) => setGameType(e.target.value as 'football' | 'basketball')}>
                    <option value="football">football</option>
                    <option value="basketball">basketball</option>
                  </select>
                </FieldRow>
                <FieldRow label="Tipoff Type">
                  <select className="form-input" value={tipoffType} onChange={(e) => setTipoffType(e.target.value as 'genius' | 'audio')}>
                    <option value="genius">genius</option>
                    <option value="audio">audio</option>
                  </select>
                </FieldRow>
                {tipoffType === 'audio' && (
                  <FieldRow label="Direction">
                    <select className="form-input" value={direction} onChange={(e) => setDirection(e.target.value as 'home' | 'away')}>
                      <option value="home">home</option>
                      <option value="away">away</option>
                    </select>
                  </FieldRow>
                )}
                <div style={rowStyle}>
                  <div style={labelStyle} />
                  <div style={{ ...fieldStyle, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={handleFetchTipoff}
                      loading={fetchingTipoff}
                      disabled={!gameName.trim() || !gameType || !tipoffType}
                    >
                      <Search size={14} />
                      Fetch Current Tipoff
                    </Button>
                    {fetchTipoffError && (
                      <span style={{ fontSize: '11px', color: Colors.error }}>{fetchTipoffError}</span>
                    )}
                  </div>
                </div>
                <FieldRow label="Tipoff Timestamp">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      type="button"
                      className="timestamp-step-button"
                      aria-label="Subtract 1 second"
                      title="-1 second (hold to keep subtracting)"
                      onMouseDown={() => startTimestampHold(-1)}
                      onMouseUp={stopTimestampHold}
                      onMouseLeave={stopTimestampHold}
                      onTouchStart={() => startTimestampHold(-1)}
                      onTouchEnd={stopTimestampHold}
                    >
                      <Minus size={14} />
                    </button>
                    <input
                      className="form-input"
                      value={timestamp}
                      onChange={(e) => setTimestamp(e.target.value)}
                      placeholder="2026-08-20T18:00:00.000Z"
                      style={{ textAlign: 'center', flex: '1 1 auto', minWidth: 0 }}
                    />
                    <button
                      type="button"
                      className="timestamp-step-button"
                      aria-label="Add 1 second"
                      title="+1 second (hold to keep adding)"
                      onMouseDown={() => startTimestampHold(1)}
                      onMouseUp={stopTimestampHold}
                      onMouseLeave={stopTimestampHold}
                      onTouchStart={() => startTimestampHold(1)}
                      onTouchEnd={stopTimestampHold}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  {originalTimestamp && (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: Colors.textLight, textAlign: 'left' }}>
                      <div>
                        Original: <span style={{ fontFamily: "'SF Mono', monospace" }}>{originalTimestamp}</span>
                      </div>
                      <div>
                        Adjusted:{' '}
                        <span
                          style={{
                            fontFamily: "'SF Mono', monospace",
                            color: timestamp !== originalTimestamp ? Colors.secondary : Colors.textLight,
                            fontWeight: timestamp !== originalTimestamp ? 700 : 400,
                          }}
                        >
                          {timestamp}
                        </span>
                      </div>
                      {tipoffType === 'audio' && (originalDetectionType || originalIsManualOverride) && (
                        <div style={{ marginTop: '4px' }}>
                          Currently tagged:{' '}
                          <span style={{ fontFamily: "'SF Mono', monospace" }}>{originalDetectionType || 'unknown'}</span>
                          {originalIsManualOverride && (
                            <span
                              style={{
                                marginLeft: '6px', fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '999px',
                                background: withAlpha(Colors.secondary, 0.14), color: Colors.secondary,
                              }}
                            >
                              manually corrected
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </FieldRow>
              </>
            )}

            {selectedOp === 'grant-access' && (
              <>
                {showEnvSelector && (
                  <FieldRow label="Environment">
                    <select className="form-input" value={env} onChange={(e) => setEnv(e.target.value as OpsEnv)}>
                      <option value="dev">dev</option>
                      <option value="qa">qa</option>
                    </select>
                  </FieldRow>
                )}
                <FieldRow label="User Email">
                  <EmailChipsInput
                    emails={emails}
                    onChange={setEmails}
                    placeholder="Type email and press Enter"
                  />
                </FieldRow>
                <FieldRow label="Game Name">
                  <SearchableSelect
                    value={gameName}
                    onChange={setGameName}
                    onOpen={loadScheduledGames}
                    options={gameSelectOptions}
                    placeholder={gamesLoading ? 'Loading scheduled games...' : 'Search scheduled games...'}
                    emptyMessage="No scheduled games found"
                    disabled={false}
                  />
                </FieldRow>
              </>
            )}

            <div style={{ ...rowStyle, marginTop: '4px' }}>
              <div style={labelStyle} />
              <div style={{ ...fieldStyle, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Button onClick={handleSubmit} loading={submitting} disabled={!isFormValid() || submitting} className="btn-primary">
                  <Send size={16} />
                  {activeOp.actionLabel}
                </Button>
                {selectedOp === 'grant-access' && (
                  <Button variant="outline" onClick={handleCheckAccess} loading={checkingAccess} disabled={emails.length === 0}>
                    Check Access
                  </Button>
                )}
              </div>
            </div>

            {result && ResultIcon && (
              <div style={{ marginLeft: '142px', maxWidth: '720px', textAlign: 'left' }}>
                {result.kind === 'check-access' && (result.checkResults?.length || 0) > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        padding: '14px 16px',
                        borderRadius: '14px',
                        background: RESULT_TONE_STYLE[result.tone].bg,
                        color: RESULT_TONE_STYLE[result.tone].fg,
                        border: `1px solid ${withAlpha(RESULT_TONE_STYLE[result.tone].fg, 0.28)}`,
                      }}
                    >
                      <ResultIcon size={18} style={{ marginTop: 1, flexShrink: 0 }} />
                      <div style={{ fontSize: '14px', fontWeight: 700 }}>{result.message}</div>
                    </div>

                    {result.checkResults!.map((checkResult) => {
                      const accessTone: ResultTone =
                        checkResult.status === 'has_access' ? 'success' : checkResult.error ? 'error' : 'warning';
                      const accessBadge = statusBadgeStyle(checkResult.status);
                      return (
                        <div
                          key={checkResult.email}
                          style={{
                            borderRadius: '14px',
                            border: '1px solid rgba(var(--color-border-rgb), 0.16)',
                            background: '#FFFFFF',
                            overflow: 'hidden',
                            boxShadow: '0 8px 18px rgba(8, 18, 52, 0.06)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px',
                              flexWrap: 'wrap',
                              padding: '12px 14px',
                              background: RESULT_TONE_STYLE[accessTone].bg,
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '13.5px', fontWeight: 700, color: Colors.text }}>{checkResult.email}</div>
                              <div style={{ marginTop: 2, fontSize: '12px', color: Colors.textLight }}>
                                {checkResult.user_id ? `User ID: ${checkResult.user_id}` : 'No linked user id'}
                                {' · '}
                                {checkResult.products.length} product{checkResult.products.length === 1 ? '' : 's'}
                              </div>
                            </div>
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                borderRadius: '999px',
                                padding: '4px 10px',
                                background: accessBadge.bg,
                                color: accessBadge.fg,
                              }}
                            >
                              {checkResult.status === 'has_access' ? 'Has access' : 'No access'}
                            </span>
                          </div>

                          {checkResult.error && (
                            <div style={{ padding: '10px 14px', fontSize: '12.5px', color: Colors.error }}>
                              {checkResult.error}
                            </div>
                          )}

                          {checkResult.products.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px' }}>
                              {checkResult.products.map((product, index) => {
                                const badge = statusBadgeStyle(product.subscription_status || 'unknown');
                                return (
                                  <div
                                    key={`${checkResult.email}-${product.product_id}-${product.game_id}-${index}`}
                                    style={{
                                      borderRadius: '12px',
                                      border: '1px solid rgba(var(--color-border-rgb), 0.16)',
                                      background: Colors.background,
                                      padding: '12px 14px',
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                                      <div style={{ fontSize: '13.5px', fontWeight: 700, color: Colors.text }}>
                                        {productLabel(product)}
                                      </div>
                                      <span
                                        style={{
                                          fontSize: '11px',
                                          fontWeight: 700,
                                          letterSpacing: '0.04em',
                                          textTransform: 'uppercase',
                                          borderRadius: '999px',
                                          padding: '4px 10px',
                                          background: badge.bg,
                                          color: badge.fg,
                                        }}
                                      >
                                        {product.subscription_status || product.purchase_mode || 'unknown'}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                                        gap: '8px 14px',
                                        marginTop: '10px',
                                        fontSize: '12px',
                                        color: Colors.textLight,
                                      }}
                                    >
                                      <div>
                                        <div style={{ fontWeight: 700, color: Colors.text, marginBottom: 2 }}>Product ID</div>
                                        <div style={{ fontFamily: "'SF Mono', monospace", wordBreak: 'break-all' }}>{product.product_id || '—'}</div>
                                      </div>
                                      <div>
                                        <div style={{ fontWeight: 700, color: Colors.text, marginBottom: 2 }}>Season</div>
                                        <div>{product.season_tag || '—'}</div>
                                      </div>
                                      <div>
                                        <div style={{ fontWeight: 700, color: Colors.text, marginBottom: 2 }}>Game ID</div>
                                        <div style={{ fontFamily: "'SF Mono', monospace", wordBreak: 'break-all' }}>{product.game_id || '—'}</div>
                                      </div>
                                      <div>
                                        <div style={{ fontWeight: 700, color: Colors.text, marginBottom: 2 }}>Expiry</div>
                                        <div>{formatExpiry(product.expiry)}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{ padding: '14px', fontSize: '13px', color: Colors.textLight }}>
                              No purchased products found for this email.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', borderRadius: '10px',
                      background: RESULT_TONE_STYLE[result.tone].bg, color: RESULT_TONE_STYLE[result.tone].fg,
                    }}
                  >
                    <ResultIcon size={18} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '13.5px', fontWeight: 600 }}>{result.message}</div>
                      <pre style={{
                        margin: '8px 0 0', fontSize: '11px', fontFamily: "'SF Mono', monospace", color: Colors.textLight,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}
                      >
                        {JSON.stringify(result.raw, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          </div>

          <div style={{ alignSelf: 'stretch', width: '1px', background: 'rgba(var(--color-border-rgb), 0.16)' }} />

          <div style={{ flex: '0 0 30%' }}>
            <ActiveGamesAccordion games={activeGameNames} loading={activeGamesLoading} />
          </div>
        </div>
      </Card>
    </div>
  );
}
